import { Route } from './api.types'
import { HealthConnect } from './HealthConnect'
import {
  RawRoutePoint,
  TimedSample,
  attachNearestSample,
  computeAvgFromSamples,
  computeElevationGainLoss,
  downsampleRoute,
  toUploadRoutePoints,
} from './appleHealthWorkoutDetails'

/**
 * Per-workout detail queries and aggregation for the Health Connect sync, mirroring the Apple
 * Health flow: heart rate samples over the workout window plus the GPS exercise route, combined
 * into the enriched ActivityEntry shape. Health Connect route locations carry no speed and there
 * is no power series, so those metrics stay absent.
 */

const HR_ATTACH_TOLERANCE_MS = 10_000

// Per-workout detail queries multiply Health Connect round-trips, so only the most recent
// workouts of a sync window get them; anything older syncs summary-only.
const MAX_DETAILED_WORKOUTS = 20

export interface HealthConnectWorkoutDetails {
  avgHeartRate: number | null
  elevationGain: number | null
  elevationLoss: number | null
  route: Route[] | null
}

/**
 * Fetches and aggregates the details of one workout. Every sub-query is best-effort: a failure
 * (missing permission, route consent not granted, no data) yields nulls for its metrics, never
 * an error. The route is only queried when `includeRoute` (the company's location-tracking
 * flag) is set.
 */
export const fetchHealthConnectWorkoutDetails = async (
  workout: { id: string; startDate: Date; endDate: Date; activityType: string },
  includeRoute: boolean,
): Promise<HealthConnectWorkoutDetails> => {
  let hrSamples: TimedSample[] = []
  try {
    const result = await HealthConnect.getHeartRate({
      startTime: workout.startDate.getTime(),
      endTime: workout.endDate.getTime(),
    })
    hrSamples = ((result.activities as any[]) || [])
      .filter((sample) => typeof sample.value === 'number')
      .map((sample) => ({ ts: new Date(sample.startDate).getTime(), value: sample.value }))
  } catch (e) {
    console.log(`HC details: HR query failed | ${workout.activityType} ${workout.id} |`, e)
    hrSamples = []
  }

  let rawRoute: RawRoutePoint[] = []
  if (includeRoute) {
    try {
      const result = await HealthConnect.getWorkoutRoute({ recordId: workout.id })
      if (result.status === 'Data') {
        rawRoute = result.points || []
      } else {
        console.log(`HC details: route unavailable (${result.status}) | ${workout.activityType} ${workout.id}`)
      }
    } catch (e) {
      console.log(`HC details: route query failed | ${workout.activityType} ${workout.id} |`, e)
      rawRoute = []
    }
  }

  console.log(
    `HC details: ${workout.activityType} ${workout.id} | hr samples: ${hrSamples.length}` +
      ` | route points: ${rawRoute.length} (includeRoute: ${includeRoute})`,
  )

  // Elevation comes from the FULL route, before downsampling.
  const elevation = computeElevationGainLoss(rawRoute)

  let route: Route[] | null = null
  if (rawRoute.length) {
    let points = downsampleRoute(toUploadRoutePoints(rawRoute))
    points = attachNearestSample(points, hrSamples, 'hr', HR_ATTACH_TOLERANCE_MS)
    route = points.length ? points : null
  }

  return {
    avgHeartRate: computeAvgFromSamples(hrSamples),
    elevationGain: elevation ? elevation.gain : null,
    elevationLoss: elevation ? elevation.loss : null,
    route,
  }
}

/**
 * Enriches the most recent Health Connect workout entries in place with client-computed metrics
 * and (when the company allows location tracking) the downsampled GPS route. Sequential on
 * purpose — Health Connect does not tolerate parallel queries well (see DataSync).
 */
export const enrichHealthConnectEntries = async (entries: any[], includeRoute: boolean): Promise<void> => {
  const recent = [...entries]
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    .slice(0, MAX_DETAILED_WORKOUTS)
  for (const entry of recent) {
    if (!entry.originalId) continue
    try {
      const details = await fetchHealthConnectWorkoutDetails(
        {
          id: entry.originalId,
          startDate: new Date(entry.startDate),
          endDate: new Date(entry.endDate),
          activityType: entry.activityType,
        },
        includeRoute,
      )
      if (details.avgHeartRate !== null) entry.avgHeartRate = details.avgHeartRate
      if (details.elevationGain !== null) entry.elevationGain = details.elevationGain
      if (details.elevationLoss !== null) entry.elevationLoss = details.elevationLoss
      if (details.route) entry.route = details.route
    } catch (error) {
      // Best-effort: a failed detail query must never block the summary sync.
      console.log('Workout detail enrichment failed', entry.originalId, error)
    }
  }
}
